import { useState } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AuthModal({ isOpen, onClose, onSuccess }: AuthModalProps) {
  const [mode, setMode] = useState<'login' | 'signup' | 'reset'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (mode === 'signup') {
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              display_name: displayName || 'Mindshift Nutzer'
            }
          }
        });

        if (signUpError) throw signUpError;

        if (data.user) {
          await supabase.from('profiles').insert({
            id: data.user.id,
            display_name: displayName || 'Mindshift Nutzer'
          });
        }

        setSuccess('Registrierung erfolgreich! Bitte bestätige deine E-Mail.');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else if (mode === 'login') {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password
        });

        if (signInError) throw signInError;

        setSuccess('Anmeldung erfolgreich!');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1000);
      } else if (mode === 'reset') {
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`
        });

        if (resetError) throw resetError;

        setSuccess('Passwort-Reset-Link wurde an deine E-Mail gesendet.');
      }
    } catch (err: any) {
      setError(err.message || 'Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {mode === 'login' && 'Willkommen zurück'}
            {mode === 'signup' && 'Starte deine Reise'}
            {mode === 'reset' && 'Passwort zurücksetzen'}
          </h2>
          <p className="text-gray-600 text-sm">
            {mode === 'login' && 'Melde dich an, um fortzufahren'}
            {mode === 'signup' && '7 Tage kostenlos testen'}
            {mode === 'reset' && 'Wir senden dir einen Reset-Link'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'signup' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Anzeigename (optional)
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Wie möchtest du genannt werden?"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Pseudonyme sind erlaubt - deine Privatsphäre ist geschützt
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              E-Mail
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="deine@email.de"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
            />
          </div>

          {mode !== 'reset' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Passwort
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
              />
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
              {success}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-teal-600 text-white py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                Lädt...
              </span>
            ) : (
              <>
                {mode === 'login' && 'Anmelden'}
                {mode === 'signup' && 'Kostenlos starten'}
                {mode === 'reset' && 'Link senden'}
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center space-y-3">
          {mode === 'login' && (
            <>
              <button
                onClick={() => setMode('reset')}
                className="text-sm text-teal-600 hover:text-teal-700 transition-colors cursor-pointer"
              >
                Passwort vergessen?
              </button>
              <div className="text-sm text-gray-600">
                Noch kein Konto?{' '}
                <button
                  onClick={() => setMode('signup')}
                  className="text-teal-600 hover:text-teal-700 font-medium transition-colors cursor-pointer"
                >
                  Jetzt registrieren
                </button>
              </div>
            </>
          )}

          {mode === 'signup' && (
            <div className="text-sm text-gray-600">
              Bereits registriert?{' '}
              <button
                onClick={() => setMode('login')}
                className="text-teal-600 hover:text-teal-700 font-medium transition-colors cursor-pointer"
              >
                Anmelden
              </button>
            </div>
          )}

          {mode === 'reset' && (
            <button
              onClick={() => setMode('login')}
              className="text-sm text-teal-600 hover:text-teal-700 transition-colors cursor-pointer"
            >
              Zurück zur Anmeldung
            </button>
          )}
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-start gap-2 text-xs text-gray-500">
            <i className="ri-shield-check-line text-teal-600 text-base flex-shrink-0"></i>
            <p>
              Deine Daten sind Ende-zu-Ende verschlüsselt und DSGVO-konform in Deutschland gespeichert.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
